import { FC, useEffect, useState } from 'react';
import './ModalUsersInsert.scss';
import ModalDefault from '../../../ModalDefault/ModalDefault';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '../../../../providers/AuthProvider';
import { set, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Col, FormGroup, Row } from 'react-bootstrap';
import ErrorMessage from '../../../ErrorMessage/ErrorMessage';
import { schema } from './Validate';
import { useUsers } from '../../../../providers/Access/Users/UsersProvider';
import { UserModel } from '../../../../models/Access/Users/UserModel';
import { Controller } from 'react-hook-form';
import ReactSelect from 'react-select';
import { useAccessProfile } from '../../../../providers/Access/AccessProfile/AccessProfileProvider';
import { customStyles } from '../../../../models/SelectCustomStyles';

interface ModalUsersInsertProps {
  show: boolean;
  onClose: () => void;
}

type FormValues = {
  id?: number;
  partner_code?: string;
  username?: string;
  password?: string;
  name?: string;
  email?: string;
  access_profile_id?: number;
}

const ModalUsersInsert: FC<ModalUsersInsertProps> = ({ show, onClose }) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [selectedAccessProfile, setSelectedAccessProfile] = useState<any>('');

  const { user, setUser, setError, handleList, handleFetch } = useUsers();
  const { accessProfilesOptions } = useAccessProfile();
  const { onInvalid } = useAuth();

  const { handleSubmit, register, setValue, control, reset, formState: { errors } } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const handleClose = () => {
    setError('');
    reset(new UserModel());
    setUser({} as UserModel);
    setSelectedAccessProfile(null);
    onClose();
  }

  const onSubmit = async (data: UserModel) => {
    setIsLoadingSubmit(true);
    setError('');

    const ret = await handleFetch(data as UserModel);

    if (ret) {
      toast.success('Usuário salvo com sucesso!');
      handleList();
      handleClose();
    }

    setIsLoadingSubmit(false);
  }

  useEffect(() => {
    if (show) {
      if (!!user?.id) {
        reset(user as FormValues);
      } else {
        reset(new UserModel());
        setUser({} as UserModel);
        setSelectedAccessProfile(null);
      }
    }
  }, [show]);

  useEffect(() => {
    setValue('access_profile_id', selectedAccessProfile?.value || '');
  }, [selectedAccessProfile]);

  useEffect(() => {
    if (user?.id && accessProfilesOptions?.length > 0) {
      setSelectedAccessProfile(accessProfilesOptions.filter((item: any) => item.value === user.access_profile?.id)?.shift());
    }
  }, [accessProfilesOptions, user]);

  return (
    <ModalDefault
      title={!!user?.id ? 'Editar Usuário' : 'Adicionar Usuário'}
      show={show}
      onClose={handleClose}
      sizeModal={'lg'}
      showFooter={true}
      buttonText={isLoadingSubmit ? 'Salvando...' : 'Salvar'}
      showButtonSpinner={isLoadingSubmit}
      disabledSubmit={isLoadingSubmit}
      handleSubmit={handleSubmit(onSubmit)}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div className="ModalUsersInsert" data-testid="ModalUsersInsert">
        <Row>
          <Col md={6}>
            <FormGroup className="form-group">
              <label htmlFor="partner_code">CÓDIGO PARCEIRO *</label>
              <input
                className={`form-control ${onInvalid(errors?.partner_code)}`}
                type='text'
                {...register('partner_code')}
                onChange={(e: any) => setUser({ ...user, partner_code: e?.target?.value })}
              />
              <ErrorMessage name={'Código Parceiro'} type={errors?.partner_code?.type?.toString()} min={3} max={150} />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="form-group">
              <label htmlFor="name">NOME *</label>
              <input
                className={`form-control ${onInvalid(errors?.name)}`}
                type='text'
                {...register('name')}
                onChange={(e: any) => setUser({ ...user, name: e?.target?.value })}
              />
              <ErrorMessage name={'Nome'} type={errors?.name?.type?.toString()} min={3} max={150} />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col>
            <FormGroup className="form-group">
              <label htmlFor="username">LOGIN *</label>
              <input
                className={`form-control ${onInvalid(errors?.username)}`}
                type='text'
                {...register('username')}
                onChange={(e: any) => setUser({ ...user, username: e?.target?.value })}
              />
              <ErrorMessage name={'Login'} type={errors?.username?.type?.toString()} min={3} max={50} />
            </FormGroup>
          </Col>
          {!user?.id && (
            <Col>
              <FormGroup className="form-group">
                <label htmlFor="password">SENHA *</label>
                <input
                  className={`form-control ${onInvalid(errors?.password)}`}
                  type='password'
                  {...register('password')}
                  onChange={(e: any) => setUser({ ...user, password: e?.target?.value })}
                />
                <ErrorMessage name={'Senha'} type={errors?.password?.type?.toString()} min={8} max={50} />
              </FormGroup>
            </Col>
          )}
        </Row>
        <Row>
          <Col md={8}>
            <FormGroup className="form-group">
              <label htmlFor="email">EMAIL *</label>
              <input
                className={`form-control ${onInvalid(errors?.email)}`}
                type='text'
                {...register('email')}
                onChange={(e: any) => setUser({ ...user, email: e?.target?.value })}
              />
              <ErrorMessage name={'Email'} type={errors?.email?.type?.toString()} min={3} max={255} />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup className="form-group">
              <label htmlFor="access_profile_id">NÍVEL DE ACESSO *</label>
              {control && (
                <Controller
                  name="access_profile_id"
                  control={control}
                  render={({ field: { onChange, value, name, ref } }) => (
                    <ReactSelect
                      ref={ref}
                      name={name}
                      isClearable
                      isSearchable
                      options={accessProfilesOptions ?? []}
                      placeholder="Selecione..."
                      className={`form-control p-0 ${onInvalid(errors?.access_profile_id)}`}
                      value={selectedAccessProfile || ''}
                      defaultValue={selectedAccessProfile}
                      onChange={val => {
                        onChange(val?.value || '');
                        setSelectedAccessProfile(val);
                      }}
                      noOptionsMessage={() => 'Não há registros'}
                      styles={customStyles}
                    />
                  )}
                />
              )}
              <ErrorMessage name={'Nível de Acesso'} type={errors?.access_profile_id?.type?.toString()} />

              <>{console.log(errors)}</>
            </FormGroup>
          </Col>
        </Row>
      </div>
    </ModalDefault>
  );
}

export default ModalUsersInsert;
